import React, {createElement, FunctionComponent, ReactElement, useCallback, useContext, useEffect, useRef, useState}
    from "react";
import { connect } from "react-redux";
import NavBar from "../navbar/NavBar";
import Projects from "../projects/Projects";
import { Box, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import Script from "../script/Script";
import { Network } from "../../fstn/network";
import ExceptionDialog from "../dialogs/Exception";
import Graph from "../graph/Graph";
import Video from "../video/Video";
import { Cursor } from "../../fstn/cursor";
import { selectMode, selectModeData, selectProject} from "../../redux/selectors/editor";
import { selectNetwork, selectProjects } from "../../redux/selectors/firebase";
import {IGraphData, ModeData, updateFocus, updateMode, updateProject, updateTitle} from "../../redux/slices/editor";
import Admin from "../admin/Admin";
import Game from "../game/Game";
import { CursorHistory } from "../cursorHistory";
import { DebugController, IDebugController } from "../../fstn/debugController";
import RegisterDialog from "../graph/RegisterDialog";
import DebugDialog from "../graph/DebugDialog";
import Writer from "../writer/Writer";
import Intertitle from "../intertitle/Intertitle";
import Help from "../help/Help";
import { clearURLSearchParam, getURLSearchParam, setURLSearchParam } from "../../utility/url";
import SheetView from "../sheets/SheetView";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {ProjectDetails} from "../../redux/types/firebase";
import ProjectDialog from "../dialogs/project/ProjectDialog";
import UserDialog from "../dialogs/user/UserDialog";
import Assets from "../assets/Assets";
import ChangeLog from "../help/ChangeLog";
import LoadingScreen from "./LoadingScreen";
import AboutDialog from "../help/AboutDialog";
import FindDialog from "../dialogs/FindDialog";
import {FSTN} from "../../fstn/fstn";
import {ActionFactory} from "../../fstn/actionFactory";
import {Localization} from "../localization/Localization"
import {Revisions} from "../revisions/Revisions";

const useStyles = makeStyles((theme) => ({
    content: {
        alignItems: "center",
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        top: 64,
        bottom: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
        "@media print": {
            top: "unset",
            bottom: "unset",
            left: "unset",
            right: "unset",
            overflow: "visible",
        },
        backgroundColor: 'lightgray',
        zIndex: 0
    },
    floatingDialog: {
        alignItems: "flex-end",
        display: "flex",
        flexDirection: "row",
        pointerEvents: "none",
        position: "absolute",
        top: "30%",
        left: theme.spacing(4),
        bottom: theme.spacing(4),
        right: theme.spacing(4),
        zIndex: 50
    }
}));

interface IProps
{
    networkData: any
    mode: string
    modeData: ModeData
    project: string
    projects: { [key: string]: ProjectDetails }
    updateFocus: (focus: string) => any
    updateMode: (mode: string) => any
    updateProject: (project: string) => void
    updateTitle: (title: string) => any
}

function Portal({networkData, mode, modeData, project, projects, updateFocus, updateMode, updateProject,
                    updateTitle}: IProps)
{
    const classes = useStyles();
    const [loaded, setLoaded] = useState(false);
    const [muted, setMuted] = useState(localStorage.getItem("muted") === "true");
    const [factory, setFactory] = useState<ActionFactory>();
    const [content, setContent] = useState<ReactElement>();
    const [cursor, setCursor] = useState<Cursor>();
    const [network, setNetwork] = useState<Network>();
    const [debugController, setDebugController] = useState<IDebugController | undefined>(undefined);
    const [showRegisters, setShowRegisters] = useState(false);
    const [openDialog, setOpenDialog] = useState("");
    const history = useRef(new CursorHistory());
    const [renderer, setRenderer] = useState<FSTN>();
    const {setSource, trackNetwork} = useContext(FirebaseContext);
    const localNetworkValid = useCallback(() =>
        networkData?.states && networkData?.initial, [networkData]);
    const saveLocalState = useCallback((cursor: Cursor) =>
    {
        const save = {
            cursor: cursor.saveGame(),
            project
        }

        localStorage.setItem("save", JSON.stringify(save));
    }, [project]);

    const handleSetMode = ({ currentTarget }: React.MouseEvent<HTMLElement>) =>
        updateMode(currentTarget.dataset.mode || "");
    const onShowDialog = (dialog: string) =>
    {
        setOpenDialog(openDialog === dialog ? "" : dialog);
    };
    const handleSetMuted = (muted: boolean) =>
    {
        if (renderer)
        {
            localStorage.setItem("muted", muted ? "true" : "false");
            setMuted(muted);
        }
    }
    const handleFocusCurrent = () =>
    {
        updateFocus(cursor?.state || "");
    };
    const handleResetNetwork = () =>
    {
        if (network && cursor && renderer)
        {
            renderer.restart().then(() =>
            {
                cursor.reset();
                history.current.Reset(cursor);
                updateFocus(cursor.state);
                saveLocalState(cursor);
            });
        }
    };
    const handleDebugEnable = (enabled: boolean) =>
    {
        const debugController = enabled ? new DebugController() : undefined;

        setDebugController(debugController);
        cursor?.setDebugController(debugController);
    }

    useEffect(() => {
        if (! project) {
            setContent(createElement(Projects));
            setLoaded(false);
            setNetwork(undefined);
        } else if (!loaded || !cursor || !factory) {
            setContent(createElement(LoadingScreen));
        } else {
            const modes: { [mode: string]: FunctionComponent<any> } = {
                admin: Admin,
                assets: Assets,
                game: Game,
                graph: Graph,
                intertitle: Intertitle,
                localization: Localization,
                revisions: Revisions,
                script: Script,
                sheet: SheetView,
                video: Video,
                writer: Writer,
                default: () => <Box m={8} p={8}><Typography variant="h4">{`No mode for "${mode}"`}</Typography></Box>
            };

            setContent(createElement(modes[mode] || modes.default,
                {cursor, debugController, factory, network, project, renderer}));
        }
    }, [cursor, debugController, factory, loaded, network, project, mode, renderer, updateMode]);
    useEffect(() =>
    {
        if (renderer)
        {
            muted || mode !== "game" ? renderer.mute() : renderer.unmute();
        }
    }, [muted, renderer, mode]);
    useEffect(() =>
    {
        if (mode && mode.length > 0)
        {
            setURLSearchParam("mode", mode);
            const modeTitle = mode.slice(0, 1).toUpperCase() + mode.slice(1);
            document.title = `Pathway - ${modeTitle}`;
        }
    }, [mode])
    useEffect(() => {
        if (projects) {
            const details = projects[project];

            if (details) {
                setSource(details.cookbook ? "cookbook" : "pathway");
                trackNetwork(project, details.filterUnconnected);
            }
        }
    }, [project, projects, setSource, trackNetwork]);
    useEffect(() =>
    {
        if (projects !== undefined)
        {
            const details = projects[project];

            if (renderer === undefined && details && localNetworkValid())
            {
                const fstn = new FSTN({
                    imagePath: details.imageSource,
                    videoPath: details.videoSource,
                    audioPath: details.audioSource
                });
                const factory = new ActionFactory(fstn.getRenderer());

                fstn.initialise(networkData, factory).then(() =>
                {
                    setCursor(fstn.getCursor());
                    setFactory(fstn.getActionFactory());
                });
                setRenderer(fstn);
            } else if (renderer?.initialised && !cursor)
            {
                setCursor(renderer.getCursor());
            }
        }
    }, [cursor, networkData, localNetworkValid, project, projects, renderer, updateFocus]);
    // Update the cursor on network change
    useEffect(() =>
    {
        if (cursor && localNetworkValid())
        {
            cursor.updateNetwork(networkData);
        }
    }, [cursor, networkData, localNetworkValid]);
    // Handle opening the project
    useEffect(() =>
    {
        if (projects && project && localNetworkValid())
        {
            clearURLSearchParam("state");
            setURLSearchParam("project", project);
        }
    }, [localNetworkValid, projects, project])
    // Handle closing the project
    useEffect(() =>
    {
        if (!project)
        {
            setCursor(undefined);

            if (loaded)
            {
                clearURLSearchParam("project", "mode");
                updateFocus("")
            }
        }
    }, [project, setCursor, loaded, updateFocus])
    useEffect(() =>
    {
        if (! localNetworkValid()) return;
        setNetwork(new Network(networkData));
    }, [networkData, localNetworkValid, setNetwork]);
    useEffect(() =>
    {
        let title = "Pathway";

        if (network) {
            if (mode === "graph") {
                const graphData = modeData as IGraphData;
                const root = network.states.get(graphData.root);

                if (root) title = root.name;
            } else {
                if (project && projects)
                {
                    title = projects[project]?.name;
                }
            }
            setLoaded(true);
        }
        updateTitle(title);
    }, [mode, modeData, network, projects, project, updateTitle]);
    useEffect(() =>
    {
        if (projects && (!project || !mode))
        {
            const save = JSON.parse(localStorage.getItem("save") || "{}");
            const requested = getURLSearchParam("project") || save.project

            if (!project) {
                updateProject(projects[requested] ? requested : "");
            }
            if (!mode) {
                updateMode(getURLSearchParam("mode") || save.mode || "intertitle");
            }
        }
    }, [mode, project, projects, updateMode, updateProject]);

    useEffect(() =>
    {
        const preventDefault = (e: DragEvent) => { e.preventDefault() };
        window.addEventListener("dragover", preventDefault);

        return () =>
        {
            window.removeEventListener("dragover", preventDefault);
        }
    });

    return (
        <>
            {project && ! loaded && <LoadingScreen/>}
            <ExceptionDialog />
            {project &&
                <Box displayPrint='none'>
                    <NavBar
                        gameTools={{
                            muted,
                            onSetMuted: handleSetMuted,
                            onSetShowDebug: handleDebugEnable,
                            onSetShowRegisters: setShowRegisters,
                            showDebug: debugController !== undefined,
                            showRegisters,
                        }}
                        graphTools={{
                            onFocusCurrent: handleFocusCurrent,
                            showDebug: debugController !== undefined,
                            showRegisters,
                            onSetShowRegisters: setShowRegisters,
                            onSetShowDebug: handleDebugEnable
                        }}
                        onResetNetwork={handleResetNetwork} onSetMode={handleSetMode} onShowDialog={onShowDialog}/>
                </Box>
            }
            <Box className={classes.content}>
                {content}
            </Box>
            <Help open={openDialog === "help"} onClose={() => setOpenDialog("")} />
            <ChangeLog open={openDialog === "changelog"} onClose={() => setOpenDialog("")} />
            <AboutDialog open={openDialog === "about"} onClose={() => setOpenDialog("")} />
            {project && <Box className={classes.floatingDialog}>
                {showRegisters &&
                    <RegisterDialog cursor={cursor} network={network} onClose={() => setShowRegisters(false)}/>}
                {debugController !== undefined &&
                    <DebugDialog
                        cursor={cursor} debugController={debugController}
                        onClose={() => setDebugController(undefined)}/>}
            </Box>}
            {openDialog === "project" && <ProjectDialog onCancel={() => setOpenDialog("")}/> }
            {openDialog === "user" && <UserDialog onClose={() => setOpenDialog("")}/> }
            {openDialog === "find" && <FindDialog onClose={() => setOpenDialog("")}/> }
        </>
    );
}
const mapStateToProps = (store: never) => ({
    mode: selectMode(store),
    modeData: selectModeData(store),
    networkData: selectNetwork(store),
    project: selectProject(store),
    projects: selectProjects(store) || {},
});

export default connect(mapStateToProps, {
    updateFocus,
    updateMode,
    updateProject,
    updateTitle,
})(Portal);
